import { useState } from "react";
import { Button, Stack } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { isFreeStack } from "@/helpers/tenant";
import { User } from "@/types";
import type { FilterValue, FilterOption } from "../../components/filters/PreSelectFilters";

interface DiscoverPredefinedFiltersProps {
  currentUser: User | null;
}

const DiscoverPredefinedFilters = ({ currentUser }: DiscoverPredefinedFiltersProps) => {
  const { searchParams, clearSearchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const [showAll, setShowAll] = useState(false);

  const PRE_SELECT_FILTER_OPTIONS: FilterOption[] = [
    {
      label: "Assigned to me",
      values: [{ owner: currentUser?.id?.toString() }],
    },
    {
      label: "Read Dormant Tables",
      values: [{ opportunity_name: "read_dormant_90d" }],
    },
    {
      label: "Zero impact queries",
      values: [{ opportunity_name: "zero_impact" }],
    },
    // "Implemented Opportunities" used to live here as a status=implemented
    // quick-filter; it's now a top-level tab backed by a separate table.
    {
      label: "Copy queries on big warehouse",
      values: [{ opportunity_name: "copy_on_big_warehouse" }],
    },
    {
      label: "Queries with remote spillage",
      values: [{ opportunity_name: "query_with_remote_spillage" }],
    },
    {
      label: "Read Dormant Tables (60 Days)",
      values: [{ opportunity_name: "read_dormant_60d" }],
    },
    {
      label: "Read Dormant Tables (30 Days)",
      values: [{ opportunity_name: "read_dormant_30d" }],
    },
  ];

  const PRE_SELECT_FILTER_OPTIONS_FREEMIUM: FilterOption[] = [
    {
      label: "Assigned to me",
      values: [{ owner: currentUser?.id?.toString() }],
    },
    // "Implemented Opportunities" is now a top-level tab — no longer
    // a quick-filter that toggles a status= search param.
  ];

  const handlePreSelect = (values: FilterValue[]) => {
    // If already selected, deselect by clearing all params
    if (isSelected(values)) {
      clearSearchParams();
      return;
    }
    // Otherwise, clear and apply new filter
    clearSearchParams();
    updateSearchParamsByKey(values.reduce((acc, value) => ({ ...acc, ...value }), {}));
  };

  const isSelected = (values: FilterValue[]) => {
    const valuesMap = new Map<string, string[]>();
    values.forEach(value => {
      const [key, val] = Object.entries(value)[0];
      if (!valuesMap.has(key)) {
        valuesMap.set(key, []);
      }
      valuesMap.get(key)!.push(String(val));
    });

    for (const [key, valArray] of valuesMap.entries()) {
      const searchParamsValues = searchParams.getAll(key);
      if (searchParamsValues.length !== valArray.length) {
        return false;
      }
      if (!searchParamsValues.every(val => valArray.includes(val))) {
        return false;
      }
    }
    const totalUniqueKeysInSearchParams = new Set(Array.from(searchParams.keys())).size;
    return totalUniqueKeysInSearchParams === valuesMap.size;
  };

  const visibleOptions = showAll
    ? PRE_SELECT_FILTER_OPTIONS
    : PRE_SELECT_FILTER_OPTIONS.slice(0, 3);

  if (isFreeStack()) {
    return (
      <Stack direction="row" gap="sm" wrap className="al-w-full">
        {PRE_SELECT_FILTER_OPTIONS_FREEMIUM.map((option, i) => (
          <Button
            className="al-font-normal"
            key={i}
            variant={isSelected(option.values) ? "default" : "secondary"}
            onClick={() => handlePreSelect(option.values)}
          >
            {option.label}
          </Button>
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap="sm" wrap className="al-w-full">
      {visibleOptions.map((option, i) => (
        <Button
          className="al-font-normal"
          key={i}
          variant={isSelected(option.values) ? "default" : "secondary"}
          onClick={() => handlePreSelect(option.values)}
        >
          {option.label}
        </Button>
      ))}
      {PRE_SELECT_FILTER_OPTIONS.length > 3 && (
        <Button
          variant="link"
          className="al-text-brand al-self-center al-ml-2 al-p-0"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Less" : "More"}
        </Button>
      )}
    </Stack>
  );
};

export default DiscoverPredefinedFilters;
